/**
 * Team create — guest checkout & "+ Create new team" entry point.
 *
 * Two modes, same page:
 *   - Guest (no JWT): coach hasn't signed in. Form collects team
 *     name + coach name + coach email. Server creates a stub User
 *     (or reuses an existing stub for that email), team, and
 *     TeamCoach owner row, then mints a fresh JWT. Routes to the
 *     confirm step.
 *   - Authenticated coach: JWT present, form collapses to team
 *     name only (we already know who you are). Same backend route,
 *     same response shape — `account_status` on the response just
 *     stays `'real'`.
 *
 * sport / age_group fields are NOT shown — they're derived
 * server-side from event.category and the division name (e.g.
 * "U10 Boys AAA" → age_group "U10"). The form stays minimal so
 * coaches can finish in under 30 seconds.
 */
import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useFlowBase } from '@/state/useFlowBase';

import type { EmbedClient } from '@/api/embed';
import { useFlow } from '@/state/FlowContext';
import { useVocab } from '@/state/vocab';
import '@/pages/TeamCreatePage.css';

interface Props {
  client: EmbedClient;
}

/** Last whitespace-separated token of a full name ("Jane Q. Smith" → "Smith"). */
function lastNameOf(fullName: string): string {
  const parts = fullName.trim().split(/\s+/).filter(Boolean);
  return parts.length ? parts[parts.length - 1] : '';
}

export function GfgTeamCreatePage({ client }: Props) {
  const { eventId, afterTeam } = useFlowBase();
  const navigate = useNavigate();
  const { selectedDivision, selectTeam } = useFlow();
  const vocab = useVocab();

  const isGuest = !client.isAuthed();
  // A coach who signed in with a new email has no name on file yet; ask for
  // it here (the server saves it) so receipts address them properly.
  const [needsName, setNeedsName] = useState(false);
  useEffect(() => {
    if (isGuest) return;
    client.getMe().then((me) => setNeedsName(me.has_name === false)).catch(() => { /* non-fatal */ });
  }, [client, isGuest]);
  const asksName = isGuest || needsName;

  const [teamName, setTeamName] = useState('');
  const [coachName, setCoachName] = useState('');
  // A guest's receipt email is the one they typed on the previous screen;
  // the field only shows if they arrived without one (e.g. a reload).
  const location = useLocation();
  const carriedEmail = ((location.state as { email?: string } | null)?.email ?? '').trim();
  const [coachEmail, setCoachEmail] = useState(carriedEmail);
  const asksEmail = isGuest && !carriedEmail.includes('@');
  // Whether the entrant name has been hand-edited. Until then, charity guests
  // get it auto-filled from the last name they type into "Your name".
  const [entryNameTouched, setEntryNameTouched] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Charity registrants we're asking a name of don't lead with an entry
  // name — we ask who they are first and default the entry name to their
  // last name.
  const charityGuest = asksName && vocab.isCharity;

  const onCoachNameChange = (value: string) => {
    setCoachName(value);
    if (charityGuest && !entryNameTouched) {
      setTeamName(lastNameOf(value));
    }
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    // Fall back to the registrant's last (or full) name if they cleared it.
    let name = teamName.trim();
    if (!name && charityGuest) {
      name = lastNameOf(coachName) || coachName.trim();
    }
    if (!name) {
      setError(`Give your ${vocab.entrant} a name.`);
      return;
    }
    if (asksName && !coachName.trim()) {
      setError('Please enter your name so we can address your receipt.');
      return;
    }
    if (isGuest) {
      if (!coachEmail.includes('@')) {
        setError('Please enter a valid email address.');
        return;
      }
    }

    setBusy(true);
    try {
      const resp = await client.createTeam({
        name,
        coach_name: asksName ? coachName.trim() : undefined,
        coach_email: isGuest ? coachEmail.trim().toLowerCase() : undefined,
        // Bulk creates the team only; events are picked next.
        event_id: eventId,
        division: eventId ? selectedDivision ?? undefined : undefined,
      });
      selectTeam(resp.team);
      navigate(afterTeam);
    } catch (err: any) {
      // 409 = real Cognito account already exists for this email. Let
      // them know explicitly and offer the sign-in fork.
      if (err?.response?.status === 409) {
        setError(
          'That email has registered before. Go back and enter it to use your saved teams.',
        );
      } else {
        const detail = err?.response?.data?.detail;
        setError(typeof detail === 'string' ? detail : 'Could not create the team. Please try again.');
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="tn-team-create tn-card">
      <h1 className="tn-team-create__title">Set up your {vocab.entrant}</h1>

      <form onSubmit={onSubmit} className="tn-team-create__form">
        {(() => {
          const entryField = (
            <>
              <label className="tn-team-create__label" htmlFor="team-name">
                {vocab.entrantCap} name{charityGuest ? ' (optional)' : ''}
              </label>
              <input
                id="team-name"
                className="tn-team-create__input"
                type="text"
                value={teamName}
                onChange={(e) => { setTeamName(e.target.value); setEntryNameTouched(true); }}
                disabled={busy}
                autoFocus={!charityGuest}
                maxLength={200}
                placeholder={vocab.namePlaceholder}
              />
            </>
          );

          const guestFields = (
            <>
              <label className="tn-team-create__label" htmlFor="coach-name">Your name</label>
              <input
                id="coach-name"
                className="tn-team-create__input"
                type="text"
                value={coachName}
                onChange={(e) => onCoachNameChange(e.target.value)}
                disabled={busy}
                autoComplete="name"
                autoFocus={charityGuest}
                maxLength={120}
                placeholder={vocab.isCharity ? 'Your full name' : 'Head coach name'}
              />

              {/* Signed-in coaches and guests from the email screen already gave it. */}
              {asksEmail ? (
                <>
                  <label className="tn-team-create__label" htmlFor="coach-email">Email for your receipt</label>
                  <input
                    id="coach-email"
                    className="tn-team-create__input"
                    type="email"
                    value={coachEmail}
                    onChange={(e) => setCoachEmail(e.target.value)}
                    disabled={busy}
                    autoComplete="email"
                    inputMode="email"
                    placeholder="you@example.com"
                  />
                </>
              ) : null}
            </>
          );

          // Charity guests: ask who they are first, then the (auto-filled)
          // entry name. Everyone else keeps the entry name up top.
          if (charityGuest) {
            return <>{guestFields}{entryField}</>;
          }
          return <>{entryField}{asksName ? guestFields : null}</>;
        })()}

        <button className="tn-team-create__submit" type="submit" disabled={busy}>
          {busy ? 'Creating…' : 'Continue'}
        </button>

        {error ? <div className="tn-team-create__error" role="alert">{error}</div> : null}
      </form>
    </div>
  );
}
